import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const WidgetSettingsPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/WidgetSettingsPage").then(
    (module) => ({ default: module.WidgetSettingsPage }),
  ),
);
const ProductSetsPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/ProductSetsPage").then(
    (module) => ({ default: module.ProductSetsPage }),
  ),
);
const ProductSetDetailPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/ProductSetsPage").then(
    (module) => ({ default: module.ProductSetDetailPage }),
  ),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const AuditPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditPage").then((module) => ({
    default: module.AuditPage,
  })),
);
const AuditDetailPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditDetailPage").then(
    (module) => ({
      default: module.AuditDetailPage,
    }),
  ),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const FacebookConnectPage = lazy(() =>
  import("@/modules/facebook/presentation/pages/FacebookConnectPage").then(
    (module) => ({
      default: module.FacebookConnectPage,
    }),
  ),
);
const FeedsManagePage = lazy(() =>
  import("@/modules/cf-feed/presentation/pages/FeedsManagePage").then(
    (module) => ({
      default: module.FeedsManagePage,
    }),
  ),
);
const FeedDetailPage = lazy(() =>
  import("@/modules/cf-feed/presentation/pages/FeedDetailPage").then(
    (module) => ({
      default: module.FeedDetailPage,
    }),
  ),
);
const FeedProductMonitorDisplayPage = lazy(() =>
  import(
    "../modules/cf-feed/presentation/pages/FeedProductMonitorDisplayPage"
  ).then((module) => ({
    default: module.FeedProductMonitorDisplayPage,
  })),
);
const PostProductsPage = lazy(() =>
  import("@/modules/cf-feed/presentation/pages/PostProductsPage").then(
    (module) => ({
      default: module.PostProductsPage,
    }),
  ),
);
const ProductDefaultCodesPage = lazy(() =>
  import("@/modules/cf-feed/presentation/pages/ProductDefaultCodesPage").then(
    (module) => ({
      default: module.ProductDefaultCodesPage,
    }),
  ),
);
const PrintFeeSettingsPage = lazy(() =>
  import("@/modules/cf-feed/presentation/pages/PrintFeeSettingsPage").then(
    (module) => ({
      default: module.PrintFeeSettingsPage,
    }),
  ),
);
const OrdersPage = lazy(() =>
  import("@/modules/cf-order/presentation/pages/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  })),
);
const MessageTemplatesPage = lazy(() =>
  import("@/modules/cf-message/presentation/pages/MessageTemplatesPage").then(
    (module) => ({
      default: module.MessageTemplatesPage,
    }),
  ),
);
const LiveListPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/LiveListPage").then(
    (module) => ({
      default: module.LiveListPage,
    }),
  ),
);
const LiveCreatePage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/LiveCreatePage").then(
    (module) => ({
      default: module.LiveCreatePage,
    }),
  ),
);
const LiveDetailPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/LiveDetailPage").then(
    (module) => ({
      default: module.LiveDetailPage,
    }),
  ),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (location.pathname === "/") {
      throw redirect({ to: "/auth/login" });
    }
  },
});
const LiveOverlayPage = lazy(() =>
  import("@/modules/cf-live/presentation/pages/LiveOverlayPage").then(
    (module) => ({ default: module.LiveOverlayPage }),
  ),
);
const liveOverlayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overlay/$id",
  component: () => (
    <LazyPage>
      <LiveOverlayPage />
    </LazyPage>
  ),
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <RequirePermissions all={["dashboard:read"]}>
      <LazyPage>
        <DashboardPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit/$id",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const feedsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feeds",
  component: () => (
    <RequirePermissions any={["feeds:read", "feeds:manage"]}>
      <LazyPage>
        <FeedsManagePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const feedDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feeds/$id",
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: () => (
    <RequirePermissions any={["feeds:read", "feeds:manage"]}>
      <LazyPage>
        <FeedDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const feedProductMonitorDisplayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feeds/$id/monitor-display",
  validateSearch: (search: Record<string, unknown>) => ({
    rotate: typeof search.rotate === "string" ? search.rotate : undefined,
  }),
  component: () => (
    <RequirePermissions any={["feeds:read", "feeds:manage"]}>
      <LazyPage>
        <FeedProductMonitorDisplayPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const postProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feeds/post-products",
  validateSearch: (search: Record<string, unknown>) => ({
    pageId: typeof search.pageId === "string" ? search.pageId : undefined,
    feedId: typeof search.feedId === "string" ? search.feedId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["products:manage"]}>
      <LazyPage>
        <PostProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productSetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/product-sets",
  validateSearch: (search: Record<string, unknown>) => ({
    pageId: typeof search.pageId === "string" ? search.pageId : undefined,
  }),
  component: () => (
    <RequirePermissions any={["live:read", "live:manage"]}>
      <LazyPage>
        <ProductSetsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productSetDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/product-sets/$id",
  validateSearch: (search: Record<string, unknown>) => ({
    pageId: typeof search.pageId === "string" ? search.pageId : undefined,
  }),
  component: () => (
    <RequirePermissions any={["live:read", "live:manage"]}>
      <LazyPage>
        <ProductSetDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productDefaultCodesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/product-default-codes",
  validateSearch: (search: Record<string, unknown>) => ({
    pageId: typeof search.pageId === "string" ? search.pageId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["products:manage"]}>
      <LazyPage>
        <ProductDefaultCodesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const printFeeSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/print-fee-settings",
  component: () => (
    <RequirePermissions all={["products:manage"]}>
      <LazyPage>
        <PrintFeeSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const widgetSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/widget-settings",
  component: () => (
    <RequirePermissions any={["live:read", "live:manage"]}>
      <LazyPage>
        <WidgetSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const messageTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/message-templates",
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: () => (
    <RequirePermissions any={["messages:read", "messages:manage"]}>
      <LazyPage>
        <MessageTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: () => (
    <RequirePermissions any={["orders:read", "orders:manage"]}>
      <LazyPage>
        <OrdersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const liveListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/live",
  component: () => (
    <RequirePermissions any={["live:read", "live:manage"]}>
      <LazyPage>
        <LiveListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const liveCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/live/new",
  validateSearch: (search: Record<string, unknown>) => ({
    draftId: typeof search.draftId === "string" ? search.draftId : undefined,
    pageId: typeof search.pageId === "string" ? search.pageId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["live:manage"]}>
      <LazyPage>
        <LiveCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const liveDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/live/$id",
  component: () => (
    <RequirePermissions any={["live:read", "live:manage"]}>
      <LazyPage>
        <LiveDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const facebookConnectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/facebook",
  validateSearch: (search: Record<string, unknown>) => ({
    facebook: typeof search.facebook === "string" ? search.facebook : undefined,
    reason: typeof search.reason === "string" ? search.reason : undefined,
    pages: typeof search.pages === "string" ? search.pages : undefined,
    missing: typeof search.missing === "string" ? search.missing : undefined,
    omitted: typeof search.omitted === "string" ? search.omitted : undefined,
  }),
  component: () => (
    <RequirePermissions any={["facebook:read", "facebook:manage"]}>
      <LazyPage>
        <FacebookConnectPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  liveOverlayRoute,
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    auditRoute,
    auditDetailRoute,
    profileRoute,
    feedsRoute,
    feedDetailRoute,
    feedProductMonitorDisplayRoute,
    postProductsRoute,
    productSetsRoute,
    productSetDetailRoute,
    productDefaultCodesRoute,
    widgetSettingsRoute,
    printFeeSettingsRoute,
    ordersRoute,
    messageTemplatesRoute,
    liveListRoute,
    liveCreateRoute,
    liveDetailRoute,
    facebookConnectRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
